import { useMemo, useState } from 'react'
import { Bot, Check, Copy, ExternalLink, ShieldCheck, Sparkles } from 'lucide-react'
import { Button, Popover, Separator } from '@/components/ds2'
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard'
import { buildTemplateAiAssistantUrl, buildTemplateAiPrompt, PASTE_AI_RESULT_LABEL, type TemplateAiAssistant } from '../utils/templateAiPrompt'
import { trackBoxTruckUiInteraction } from '../utils/analytics'
import { DesignPasteButton } from './DesignPasteButton'

interface TemplateAiActionsProps {
    subject: string
    html: string
    /** Applies the AI's answer from the clipboard; returns whether it was used. */
    onPasteResult: (plainText: string, html: string) => boolean
}

export const TemplateAiActions = ({ subject, html, onPasteResult }: TemplateAiActionsProps) => {
    const [open, setOpen] = useState(false)
    const prompt = useMemo(() => buildTemplateAiPrompt(subject, html), [subject, html])
    const { copiedKey, copy } = useCopyToClipboard({ errorMessage: 'Could not copy the AI editing prompt' })

    const copyPrompt = () => {
        trackBoxTruckUiInteraction('template_ai_opened', 'copy_prompt')
        void copy(prompt, 'template-ai')
    }
    const openAssistant = (assistant: TemplateAiAssistant) => {
        trackBoxTruckUiInteraction('template_ai_opened', assistant)
        window.open(buildTemplateAiAssistantUrl(assistant, prompt), '_blank', 'noopener,noreferrer')
    }
    // Closed on success so the preview the paste switched to is what the operator sees next.
    const pasteResult = (plainText: string, pastedHtml: string): boolean => {
        const applied = onPasteResult(plainText, pastedHtml)
        if (applied) setOpen(false)
        return applied
    }

    return (
        <Popover
            title="Edit template with AI"
            open={open}
            onOpenChange={setOpen}
            trigger={
                <Button variant="secondary" size="sm" prefix={<Sparkles size={16} />}>
                    Edit with AI
                </Button>
            }
        >
            <div className="flex w-[400px] max-w-[calc(100vw-48px)] flex-col gap-3">
                <ol className="list-decimal space-y-1.5 pl-5 text-sm text-foreground">
                    <li>Open ChatGPT or Claude below. Your template and the instructions are already filled in.</li>
                    <li>At the bottom of the message, replace the placeholder with the change you want, then send it.</li>
                    <li>Copy the AI’s whole answer with its Copy button.</li>
                    <li>Come back here and click “{PASTE_AI_RESULT_LABEL}”. You will see a preview before anything is saved.</li>
                </ol>

                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        variant="secondary"
                        size="sm"
                        prefix={<Bot size={16} />}
                        suffix={<ExternalLink size={14} />}
                        onClick={() => openAssistant('chatgpt')}
                    >
                        Open ChatGPT
                    </Button>
                    <Button
                        variant="secondary"
                        size="sm"
                        prefix={<Bot size={16} />}
                        suffix={<ExternalLink size={14} />}
                        onClick={() => openAssistant('claude')}
                    >
                        Open Claude
                    </Button>
                    <Button variant="ghost" size="sm" prefix={copiedKey === 'template-ai' ? <Check size={16} /> : <Copy size={16} />} onClick={copyPrompt}>
                        {copiedKey === 'template-ai' ? 'Copied' : 'Copy prompt'}
                    </Button>
                </div>

                <Separator />

                <div className="flex flex-col gap-2">
                    <p className="text-sm font-medium text-foreground">Got the AI’s answer?</p>
                    <DesignPasteButton label={PASTE_AI_RESULT_LABEL} variant="primary" onPaste={pasteResult} />
                </div>

                <div className="flex items-start gap-2 rounded-xl bg-success-soft p-3 text-xs text-success-soft-foreground">
                    <ShieldCheck className="mt-0.5 size-4 shrink-0" />
                    <span>The AI’s answer is checked here first. Anything unsafe is refused, and nothing is saved until you click Save template.</span>
                </div>
            </div>
        </Popover>
    )
}
